/**
 * Types for Chat Message API Response
 */

import { ChatAgentType } from '@constants/chat-agents';

/**
 * Reference information for message sources
 */
export interface MessageReference {
  uri: string;
  title: string;
  description?: string;
}
export enum SenderType {
  BOT = 'BOT',
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum ReactionType {
  LIKE = 'LIKE',
  DISLIKE = 'DISLIKE',
}

export interface CommandVariable {
  name: string;
  type: 'BOOLEAN' | 'STRING' | 'NUMBER' | string;
  value: string;
}


export interface MessageButton {
  id: number;
  name: string;
  content: string;
  type: 'BUSINESS' | string;
  commandVariables?: CommandVariable[];
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  enabledColor?: boolean;
  isDisplay?: boolean;
  shortCut?: string;
  intent?: string;
  isHasPermission?: boolean;
}

export interface ForwardedImage {
  imageUrl: string;
  contentType: string;
  uploadStatus: string;
  geminiFileUri?: string;
}

export interface ProductInfo {
  name: string;
  quantity: string;
  price: string;
  totalAmount: string;
}

export interface OrderInfoPayload {
  level?: number;
  products: ProductInfo[];
  order?: number;
  paymentMethod?: string;
}

export interface ProductListItem {
  productId?: string;
  name?: string;
  productCode?: string;
  quantity?: string | number;
  category?: string;
  price?: string;
  image?: string;
  productDetail?: string;
  addToCart?: boolean;
  outofstock?: boolean;
  outOfStock?: boolean;
}

export interface ProductListPayload {
  level?: number;
  products: ProductListItem[];
  order?: number;
}

export type CategoryProductIntent = 'analyze_category' | 'suggest_product';

export interface CategoryProductItem {
  id?: string;
  name: string;
  imageUrl?: string;
  sellingPrice?: number;
  totalRevenue?: number;
  status?: boolean;
  failureReason?: string;
  orderNumber?: number;
}

export interface CategoryProductLink {
  text: string;
  url: string;
}

export interface CategoryProductData {
  label: string;
  products: CategoryProductItem[];
  footerText?: string;
  links: CategoryProductLink[];
  buttons: MessageButton[];
}

export type PaymentAccountStatus = 'SUCCESS' | 'FAIL' | 'INVALID_INFO';

export interface PaymentAccountItem {
  id?: string;
  bankCode?: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  iconUrl?: string;
  accountType?: string;
  isValid: boolean;
  status?: PaymentAccountStatus | boolean;
  errorMessage?: string;
}

export interface PaymentAccountData {
  title: string;
  items: PaymentAccountItem[];
  buttons: MessageButton[];
  links: CategoryProductLink[];
  isResult: boolean;
}

export interface PaymentAccountPayload {
  data?: unknown[];
  buttons?: MessageButton[];
  order?: number;
}

export interface CategoryProductPayload {
  level?: number;
  order?: number;
  title?: string;
  text?: string;
  message?: string;
  products?: unknown[];
  items?: unknown[];
  data?: unknown[] | null;
  featuredProducts?: unknown[];
  featured_products?: unknown[];
}

export interface DividerPayload {
  level?: number;
  order?: number;
}

export interface TextBlockPayload {
  text?: string; // Made optional as some blocks might not have text
  isBold?: boolean;
  level?: number;
  references?: MessageReference[];
  followUpQuestions?: string[];
  order?: number;
  buttons?: MessageButton[];
  paymentMethod?: string;
  status?: string; // Status field for order info
}

/** blockContents item when type === "IMAGES" — backend may vary; parser accepts several shapes */
export interface ImagesBlockPayload {
  urls?: string[];
  imageUrls?: string[];
  fileUrls?: string[];
  images?: Array<{ url?: string; src?: string }>;
  items?: Array<{ url?: string; src?: string }>;
  imageUrl?: string;
  order?: number;
  /** Nested payload wrapper from some backends */
  data?: Record<string, unknown>;
}

export interface RevenueMetric {
  value: number;
  rate: number;
  direction: 'up' | 'down' | 'none' | string;
  suffix: string;
  formattedValue: string;
}

export interface RevenueTopProduct {
  name: string;
  quantity: string;
  amount: string;
}

export interface RevenueReportData {
  startDate: string;
  endDate: string;
  revenue: RevenueMetric;
  orders: RevenueMetric;
  avgOrder: RevenueMetric;
  topProducts: RevenueTopProduct[];
  buttons: MessageButton[];
}

export interface RevenueReportPayload {
  level?: number;
  order?: number;
  data: RevenueReportData;
}

export interface ChartDataPoint {
  index: number;
  time: string;
  value: number;
  lastPeriodValue: number;
  lastPeriodTime: string;
}

export interface ProfitLossReportData {
  startDate: string;
  endDate: string;
  timeRange: string;
  totalRevenue: number;
  totalCost: number;
  totalProfit: number;
  totalProfitOther: number;
  totalProfitRate: number;
  totalRevenueRate?: number;
  totalCostRate?: number;
  totalProfitOtherRate?: number;
  reportType: string;
}

export interface ProfitLossReportPayload {
  level?: number;
  order?: number;
  data: ProfitLossReportData;
}

export interface IncomeExpenseReportData {
  startDate: string;
  endDate: string;
  timeRange: string;
  totalCashIn: number;
  totalCashOut: number;
  remindCash: number;
  avgPerDay: number;
  compareCashWithLastPeriod: number;
  chartDataDetails: ChartDataPoint[];
  reportType: string;
}

export interface IncomeExpenseReportPayload {
  level?: number;
  order?: number;
  data: IncomeExpenseReportData;
}

export interface StockReportData {
  date?: string;
  reportType?: string;
  totalQuantity?: number;
  totalAmount?: number;
  totalOutOfStockQuantity?: number;
  totalCanSellQuantity?: number;
}

export interface StockReportPayload {
  level?: number;
  order?: number;
  data: StockReportData;
}

export interface ComponentInfoPayload {
  text?: string;
  order?: number;
  attachmentType?: string;
  attachment_type?: string;
  /** History shape from BE: payload.data.attachmentType */
  data?: {
    attachmentType?: string;
    attachment_type?: string;
  };
  imageUrl?: string;
  avatarUrl?: string;
}

export interface BlockContent {
  type: 'TEXT' | 'ORDER_INFO' | 'DIVIDER' | 'PRODUCT_LIST' | 'REVENUE_REPORT' | 'PROFIT_LOSS_REPORT' | 'INCOME_EXPENSE_REPORT' | 'STOCK_REPORT' | 'COMPONENT_INFO' | string;
  payload: TextBlockPayload | OrderInfoPayload | DividerPayload | ProductListPayload | CategoryProductPayload | PaymentAccountPayload | RevenueReportPayload | ProfitLossReportPayload | IncomeExpenseReportPayload | StockReportPayload | ComponentInfoPayload;
}


export enum INTENT_TYPES {
  REVENUE_REPORT = 'revenue_report',
  COST_REPORT = 'cost_report',
  INVENTORY_VALUE_REPORT = 'inventory_value_report',
  OUT_OF_STOCK_REPORT = 'out_of_stock_report',
  PROFIT_REPORT = 'profit_report',
  QUANTITY_REPORT = 'quantity_report',
}
export type TypeChangeFlow = 'FLOW_AGENT_PENDING' | string;
export type IntentType = INTENT_TYPES | string;

export interface ChatMessageResponse {
  id: string;
  idString?: string;
  isHidden: boolean;
  isHasReactionMessage: boolean;
  messageId: string;
  messageIdStr?: string;
  conversationId: number;
  businessId: string;
  blockContents: BlockContent[];
  senderType: SenderType;
  fullContent: string;
  createdAt: string;
  updatedAt: string;
  sendMessageUser: boolean;
  typeChat: ChatAgentType | string;
  typeChangeFlow?: TypeChangeFlow;
  isSynced: boolean;
  isError?: boolean;
  isFinal: boolean;
  typeMessage: 'text' | string;
  audio?: string;
  audioRawText?: string;
  intent?: IntentType;
  isWriteLog: boolean;
  targetAgent?: ChatAgentType | string;
  msgUserFwToNewAgent?: string;
  msgBotFwToNewAgentId?: string;
  msgUserFwCodeMessage?: string;
  msgUserImagesFw?: ForwardedImage[];
  /** Forwarded component attachments (same wire shape as sendChatMessage attachments). */
  msgUserAttachmentsFw?: import('./chat-component').SocketComponentAttachment[];
  codeMessage?: string;
  suggestAgentNotify?: string;
  buttons?: MessageButton[];
  references?: MessageReference[];
  followUpQuestions?: string[];
  // Reply/Quote fields from backend
  replyToMessageId?: string | number;
  replyToMessage?: any;
  replyToMessageIdStr?: string;
  // Like/Dislike state from backend
  actionMessage?: 'LIKE' | 'DISLIKE' | null;
  // Response delivery mode from backend ('single' = full message at once, 'stream' = token-by-token)
  responseMode?: 'single' | 'chunk' | string;

  /**
   * UI hint: when true, bypass typewriter animation and render the message fully.
   * Used for non-history forwarded messages (e.g. "Tại đây" forward).
   */
  skipAnimation?: boolean;
}


export interface ChatHistoryResponse {
  data: {
    data: ChatMessageResponse[];
    nextCursor?: string;
    hasMore?: boolean;
  };
}
